import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import {
  ArrowRight,
  Bell,
  FileLock2,
  HeartHandshake,
  ShieldCheck,
  Users,
  Wrench,
} from "lucide-react";
import { AppHeader } from "@/components/site/AppHeader";
import { SavedHomes } from "@/components/home/SavedHomes";
import { listMyBookings } from "@/lib/bookings.functions";
import { listHomeServiceReminders } from "@/lib/homes.functions";
import { getMyMembership } from "@/lib/memberships.functions";
import { PageHeader, PageShell } from "@/components/ui/shields-layout";

export const Route = createFileRoute("/_authenticated/home")({
  // Retired home-management hub: data is kept, screens are hidden.
  beforeLoad: () => { throw redirect({ to: "/guides" }); },
  head: () => ({
    meta: [
      { title: "My Home — Shields" },
      {
        name: "description",
        content: "Your properties, maintenance, projects and protected home records.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: async () => {
    const [bookings, reminders, membership] = await Promise.all([
      listMyBookings(),
      listHomeServiceReminders(),
      getMyMembership(),
    ]);
    return { bookings, reminders, membership };
  },
  component: MyHomePage,
});

function MyHomePage() {
  const { bookings, reminders } = Route.useLoaderData();
  const today = new Date().toISOString().slice(0, 10);
  const completed = bookings.filter((booking: any) =>
    ["completed", "closed"].includes(booking.status),
  );
  const active = bookings.filter(
    (booking: any) =>
      !["completed", "closed", "cancelled", "declined", "expired"].includes(
        booking.status,
      ),
  );
  const due = reminders.filter(
    (reminder: any) =>
      reminder.status === "active" && reminder.due_date <= today,
  );

  const items = [
    {
      to: "/home/maintenance" as const,
      icon: Bell,
      title: "Reminders",
      detail: `${reminders.length} upcoming`,
      accent: "purple",
    },
    {
      to: "/home/history" as const,
      icon: Wrench,
      title: "Service History",
      detail: `${completed.length} completed job${completed.length === 1 ? "" : "s"}`,
      accent: "gold",
    },
    {
      to: "/home/vault" as const,
      icon: FileLock2,
      title: "Documents",
      detail: "Certificates, warranties & manuals",
      accent: "blue",
    },
    {
      to: "/home/partners" as const,
      icon: HeartHandshake,
      title: "Previous Partners",
      detail: "Book trusted professionals again",
      accent: "navy",
    },
    {
      to: "/home/household" as const,
      icon: Users,
      title: "Household Sharing",
      detail: "Manage your home together",
      accent: "navy",
    },
  ] as const;

  return (
    <div className="min-h-screen bg-background pb-24">
      <AppHeader workspace="customer" />
      <PageShell width="narrow">
        <PageHeader eyebrow="Your home, handled" title="My Home" description="Keep your property, maintenance, documents and service history together." />

        <SavedHomes />

        <section className="mt-5 rounded-2xl border border-gold/35 bg-gold/5 p-4">
          <Link to="/home/health" search={{ homeId: undefined }} className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <strong className="block text-foreground">Home Health</strong>
              <small className="mt-1 block text-sm text-muted-foreground">
                {due.length
                  ? `${due.length} item${due.length === 1 ? "" : "s"} need attention`
                  : "No recorded reminders are overdue"}
              </small>
            </span>
            <ArrowRight className="h-5 w-5 text-muted-foreground" />
          </Link>
        </section>

        <section className="mt-5 space-y-2">
          {items.map(({ to, icon: Icon, title, detail, accent }) => (
            <Link
              key={title}
              to={to}
              search={{ homeId: undefined }}
              className="card-surface flex min-h-17 items-center gap-3 p-4" >
              <span
                className={[
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                  accent === "gold" ? "bg-gold/15 text-gold" : "bg-primary-soft text-primary",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <strong className="block text-foreground">{title}</strong>
                <small className="mt-0.5 block truncate text-sm text-muted-foreground">
                  {detail}
                </small>
              </span>
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </Link>
          ))}
        </section>

        <section className="mt-6 grid gap-3 sm:grid-cols-2">
          <Link
            to="/book"
            className="flex min-h-14 items-center justify-center rounded-2xl bg-primary px-4 text-sm font-bold text-primary-foreground"
          >
            Book a Service
          </Link>
          <Link
            to="/home/calendar"
            search={{ homeId: undefined }}
            className="flex min-h-14 items-center justify-center rounded-2xl border border-border bg-card px-4 text-sm font-bold text-foreground"
          >
            Open Home Calendar
          </Link>
        </section>

        <p className="mt-5 text-center text-xs leading-5 text-muted-foreground">
          {active.length
            ? `${active.length} active Shields job${active.length === 1 ? "" : "s"} connected to your account.`
            : "Completed Shields work stays in your private service history."}
        </p>
      </PageShell>
    </div>
  );
}
